import { getOrgIdFromHostname, getOrgSlugFromHostname, isCustomDomain } from '@/core/domain';
import { applyCfCookies, rewriteResourceUrls } from '@/core/resources';
import { getAuthToken, getLinkSessionId } from './authHeader';
import { isTauri } from '@/lib/offline/isTauri';
import { tauriFetch } from '@/lib/offline/tauriFetch';
import jsonpack from 'jsonpack';
import type { TypedDocumentString } from './graphql';

export { getAuthToken, getLinkSessionId } from './authHeader';

export async function execute<TResult, TVariables>(
  query: TypedDocumentString<TResult, TVariables>,
  ...[variables]: TVariables extends Record<string, never> ? [] : [TVariables]
) {
  const url = new URL(import.meta.env.VITE_ENDPOINT);
  url.pathname = '/gql';

  const orgId = getOrgIdFromHostname();
  const slug = getOrgSlugFromHostname();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/graphql-response+json',
    Authorization: getAuthToken(),
  };

  const linkSessionId = getLinkSessionId();
  if (linkSessionId) headers['X-Link-Session'] = linkSessionId;

  if (orgId) headers['x-organisation'] = orgId;
  else if (slug) headers['X-Organization-Slug'] = slug;

  const projectMatch = window.location.pathname.match(/^\/projects\/([^/]+)/);
  if (projectMatch) headers['x-project-id'] = projectMatch[1];

  // In Tauri the WKWebView can't carry the auth/CloudFront cookies cross-origin,
  // so route the API through Rust (`tauriFetch`) and its shared cookie jar.
  const doFetch = isTauri() ? tauriFetch : fetch;
  const response = await doFetch(url.toString(), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) throw new Error('Network response was not ok');

  const body = await response.text();
  let res;
  try {
    res = JSON.parse(body);
  } catch {
    try {
      res = jsonpack.unpack(body) as any;
    } catch (error) {
      throw error;
    }
  }

  // Foreign (custom-domain) origins can't receive the CloudFront signed
  // cookies via response headers (Set-Cookie is scrubbed cross-origin), so
  // Task 6's resolver ships them in extensions instead — apply them here,
  // first-party on the current origin. skygis.app origins get them via
  // headers already, so don't double-apply from this path.
  if (res?.extensions?.cfCookies && isCustomDomain()) applyCfCookies(res.extensions.cfCookies);
  // Partial-success responses ({ data, errors }) intentionally return data —
  // pre-existing contract predating the custom-domain change; react-query
  // callers rely on getting partial data instead of a hard failure.
  if (res?.data) return rewriteResourceUrls(res.data) as TResult;
  if (res?.errors) throw new Error(res.errors[0].message);
  throw new Error('Empty GraphQL response');
}
